import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { fmtDate, titleCase } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/permissions";

export default function SupportCaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  // The legacy admin-user directory is retired. Assignment stays unavailable until the
  // support API provides a permission-scoped assignee directory.
  const agents = [];
  const [message, setMessage] = useState("");
  const [internal, setInternal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAgent, setAssignAgent] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/support-cases/${id}`);
      setC(r.data);
    } catch {
      toast.error("Failed to load case");
    }
  };
  useEffect(() => { load(); }, [id]);

  const sendMessage = async () => {
    if (!message.trim()) return;
    try {
      await api.post(`/support-cases/${id}/message`, { message, is_internal: internal });
      setMessage(""); setInternal(false);
      load();
    } catch {
      toast.error("Failed to send message");
    }
  };

  const changePriority = async (priority) => {
    try {
      await api.post(`/support-cases/${id}/priority`, { priority });
      toast.success("Priority updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const assign = async () => {
    if (!assignAgent) return;
    try {
      await api.post(`/support-cases/${id}/assign`, { agent_id: assignAgent });
      toast.success("Case assigned");
      setAssignOpen(false); setAssignAgent("");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const resolve = async () => {
    if (!resolution.trim()) return;
    try {
      await api.post(`/support-cases/${id}/resolve`, { resolution });
      toast.success("Case resolved");
      setResolveOpen(false); setResolution("");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const close = async (payload) => {
    try {
      await api.post(`/support-cases/${id}/close`, { note: payload.reason });
      toast.success("Case closed");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  if (!c) return <div className="py-10 text-sm text-muted-foreground">Loading…</div>;
  const canAct = can(user, "case.resolve");

  return (
    <div data-testid="support-case-detail-page">
      <button onClick={() => nav("/support")} className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-cases">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to support cases
      </button>
      <PageHeader
        title={<span><span className="font-mono text-base">{c.case_id}</span> — {c.subject}</span>}
        description={`${titleCase(c.type)} · ${c.requester_type ? titleCase(c.requester_type) : "Support ticket"}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={c.priority} />
            <StatusBadge status={c.status} />
            {canAct && (
              <>
                {agents.length > 0 && <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} data-testid="assign-btn">Assign</Button>}
                <Select value={c.priority} onValueChange={changePriority}>
                  <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{titleCase(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {c.status !== "resolved" && c.status !== "closed" && (
                  <Button size="sm" onClick={() => setResolveOpen(true)} data-testid="resolve-btn"><CheckCircle2 className="w-4 h-4" /> Resolve</Button>
                )}
                {c.status !== "closed" && (
                  <Button size="sm" variant="destructive" onClick={() => setConfirmDialog({ action: "close", title: "Close case?", confirm: "Close", reasonRequired: true, destructive: true })} data-testid="close-btn"><X className="w-4 h-4" /> Close</Button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="space-y-4">
          <InfoCard title="Case">
            <Row label="Type" value={titleCase(c.type)} />
            <Row label="Priority" value={<StatusBadge status={c.priority} />} />
            <Row label="Status" value={<StatusBadge status={c.status} />} />
            <Row label="Assigned" value={c.assigned_agent_name || "Unassigned"} />
            <Row label="Created" value={fmtDate(c.created_at, true)} />
          </InfoCard>
          <InfoCard title="Linked">
            <Row label="Booking" value={c.booking_id ? <Link to={`/bookings/${c.booking_id}`} className="hover:underline font-mono">{c.booking_reference}</Link> : "—"} />
            <Row label="Customer" value={c.customer_id ? <Link to={`/customers/${c.customer_id}`} className="hover:underline">{c.customer_name}</Link> : "—"} />
            <Row label="Supplier" value={c.supplier_id ? <Link to={`/suppliers/${c.supplier_id}`} className="hover:underline">{c.supplier_name}</Link> : "—"} />
          </InfoCard>
          {c.resolution && (
            <InfoCard title="Resolution">
              <p className="text-sm">{c.resolution}</p>
            </InfoCard>
          )}
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-lg flex flex-col" data-testid="case-conversation">
          <div className="px-5 py-3 border-b border-border"><h3 className="text-sm font-semibold">Conversation</h3></div>
          <div className="flex-1 divide-y divide-border max-h-[480px] overflow-y-auto">
            <div className="px-5 py-3 bg-muted/30">
              <div className="text-xs text-muted-foreground mb-1">{fmtDate(c.created_at, true)}</div>
              <div className="text-sm">{c.description || "—"}</div>
            </div>
            {(c.conversation || []).map((m, i) => (
              <div key={i} className={`px-5 py-3 ${m.is_internal ? "bg-[hsl(var(--status-warning-bg))/0.5]" : ""}`}>
                <div className="text-xs text-muted-foreground flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">{m.author}{m.is_internal && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning-text))] border border-[hsl(var(--status-warning-border))]">Internal</span>}</span>
                  <span>{fmtDate(m.timestamp, true)}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{m.message}</div>
              </div>
            ))}
          </div>
          {canAct && c.status !== "closed" && (
            <div className="border-t border-border p-4 space-y-2">
            <Textarea placeholder="Reply to the requester…" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="message-input" />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-checkbox" />
                  Internal note flag is ignored by the live ticket API
                </label>
                <Button size="sm" onClick={sendMessage} disabled={!message.trim()} data-testid="send-message-btn"><Send className="w-3.5 h-3.5" /> Send</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Resolve case</DialogTitle></DialogHeader>
          <Label className="text-xs">Resolution summary <span className="text-destructive">*</span></Label>
          <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={4} data-testid="resolution-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button onClick={resolve} disabled={!resolution.trim()} data-testid="resolve-submit-btn">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assign case</DialogTitle></DialogHeader>
          <Label className="text-xs">Agent</Label>
          <Select value={assignAgent} onValueChange={setAssignAgent}>
            <SelectTrigger data-testid="assign-agent-select"><SelectValue placeholder="Choose agent" /></SelectTrigger>
            <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {titleCase(a.role)}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assign} disabled={!assignAgent} data-testid="assign-submit-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDialog}
        onOpenChange={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        confirmLabel={confirmDialog?.confirm}
        requireReason={confirmDialog?.reasonRequired}
        destructive={confirmDialog?.destructive}
        onConfirm={close}
      />
    </div>
  );
}

function InfoCard({ title, children }) {
  return <div className="bg-card border border-border rounded-lg p-5"><h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{title}</h3><dl className="space-y-2">{children}</dl></div>;
}
function Row({ label, value }) {
  return <div className="flex items-start justify-between gap-3"><dt className="text-xs text-muted-foreground shrink-0">{label}</dt><dd className="text-sm text-right break-all">{value || "—"}</dd></div>;
}
